import { assertSafe, deepFreeze } from "./schema.js";
import { getDefinition, validateOptions, commonFields } from "./registry.js";
import {
  marinaAccent,
  marinaPageCards,
  marinaThemes,
} from "./marina-theme.generated.js";
// Light and Dark are the Marina tokens (cloud ADR 0396, TOM-676; VENDORED.md
// local change 14), generated from docs/design/marina-system/tokens.json with
// cloud/web's colours. Night stays as it was, red on near-black (ADR 0393 §5).
// Bundled fonts keep web/iOS glyph metrics consistent.
export const themes = {
  dark: { ...marinaThemes.dark },
  light: { ...marinaThemes.light },
  night: {
    canvas: "#100302",
    background: "#1e0604",
    surface: "#2a0905",
    text: "#ef9a89",
    muted: "#b96d60",
    quiet: "#8d4b42",
    grid: "#52251e",
    track: "#6b3027",
    good: "#b96d60",
    attention: "#d95a45",
    danger: "#ef9a89",
    sky: "#532828",
    earth: "#321417",
  },
};
export const accents = {
  coral: marinaAccent.dark,
  cyan: "#5ed4ec",
  blue: "#7cabff",
  green: "#6cdda7",
  orange: "#ffc477",
  purple: "#c4a1ff",
  red: "#ff8e99",
};
export function themeFor(options) {
  const t = { ...themes[options.theme ?? "dark"] };
  t.accent =
    options.theme === "night"
      ? "#d95a45"
      : options.theme === "light"
        ? {
            coral: marinaAccent.light,
            cyan: "#087f99",
            blue: "#245dc2",
            green: "#087342",
            orange: "#995700",
            purple: "#6c3abb",
            red: "#b52847",
          }[options.accent ?? "coral"]
        : accents[options.accent ?? "coral"];
  return t;
}
export const builtinSets = [
  {
    id: "tombolo.detailed",
    version: 1,
    title: "Neomaris Detailed",
    defaults: {
      variant: "detailed",
      accent: "coral",
      animation: "smooth",
      animationDuration: 1400,
    },
  },
  {
    id: "tombolo.simple",
    version: 1,
    title: "Neomaris Simple",
    defaults: {
      variant: "simple",
      accent: "coral",
      animation: "smooth",
      animationDuration: 1000,
    },
  },
];
const legacySetIds = {
  "tombolo.standard": "tombolo.detailed",
  "tombolo.classic": "tombolo.detailed",
  "tombolo.minimal": "tombolo.simple",
};
export class PersonalSetRegistry {
  constructor(session) {
    this.session = session;
    this.privateSets = new Map();
  }
  register(set) {
    assertSafe(set);
    for (const [key, value] of Object.entries(set.defaults ?? {})) {
      if (!commonFields[key])
        throw new Error(`Unsupported common set field: ${key}`);
      validateOptions("number", { [key]: value });
    }
    for (const [id, config] of Object.entries(set.controls ?? {})) {
      getDefinition(id);
      if (Object.keys(config).some((k) => k !== "options"))
        throw new Error("Set controls contain only visual options");
      validateOptions(id, config.options ?? {});
    }
    if (!this.session()?.accountId) throw new Error("Authentication required");
    if (
      !set?.id ||
      !Number.isInteger(set.version) ||
      set.version < 1 ||
      typeof set.title !== "string"
    )
      throw new Error("Versioned set identity required");
    if (
      Object.keys(set).some(
        (k) => !["id", "version", "title", "defaults", "controls"].includes(k),
      )
    )
      throw new Error("Sets contain only declarative fields");
    if (
      builtinSets.some((s) => s.id === set.id) ||
      Object.hasOwn(legacySetIds, set.id)
    )
      throw new Error("Shared built-in sets are immutable");
    const key = JSON.stringify([this.session().accountId, set.id, set.version]);
    if (this.privateSets.has(key))
      throw new Error("Set revisions are immutable");
    this.privateSets.set(key, structuredClone(set));
    return structuredClone(set);
  }
  get(id, version = 1) {
    id = Object.hasOwn(legacySetIds, id) ? legacySetIds[id] : id;
    const built = builtinSets.find((s) => s.id === id && s.version === version);
    return structuredClone(
      built ??
        this.privateSets.get(
          JSON.stringify([this.session()?.accountId, id, version]),
        ) ??
        null,
    );
  }
  list() {
    return [
      ...builtinSets.map((s) => structuredClone(s)),
      ...[...this.privateSets]
        .filter(([k]) => JSON.parse(k)[0] === this.session()?.accountId)
        .map(([, s]) => structuredClone(s)),
    ];
  }
}
export function adoptSet(instance, set) {
  assertSafe(set);
  if (!set?.id || !Number.isInteger(set.version) || set.version < 1)
    throw new Error("Versioned set identity required");
  const next = structuredClone(instance);
  next.setId = set.id;
  next.setVersion = set.version;
  next.options = validateOptions(
    instance.definitionId,
    {
      ...instance.options,
      ...set.defaults,
      ...set.controls?.[instance.definitionId]?.options,
    },
    true,
  );
  return next;
}
/**
 * The card the page check paints under a page, per theme: the lightest point
 * of the card's glass over the header scene (cloud ADR 0396), so a page is
 * judged against the brightest card it can sit on.
 */
export const pageCards = { ...marinaPageCards };
deepFreeze(themes);
deepFreeze(pageCards);
deepFreeze(accents);
deepFreeze(builtinSets);
